import { useCallback, useEffect, useRef } from 'react';
import { usePreviewKeyBoardContext } from './usePreviewKeyBoardProvider';

export function usePreviewKeyBoardEvents(
    isPreviewVisible: boolean,
    maxPreviewLength: number,
) {
    const currentKeyboardIndex = useRef(0);
    const { setCurrentPreviewIndexFocused, indexFocusedByClick } =
        usePreviewKeyBoardContext();

    const handleFocus = useCallback(
        (direction: string): void => {
            switch (direction) {
                case 'ArrowDown':
                    // Increment currentPreviewIndexFocused
                    if (currentKeyboardIndex.current < maxPreviewLength) {
                        currentKeyboardIndex.current++;
                    }
                    break;
                case 'ArrowUp':
                    // Decrement currentPreviewIndexFocused
                    if (currentKeyboardIndex.current > 1) {
                        currentKeyboardIndex.current--;
                    }
                    break;
                default:
                    // Do nothing
                    break;
            }
            setCurrentPreviewIndexFocused(currentKeyboardIndex.current);
        },
        [maxPreviewLength, currentKeyboardIndex, setCurrentPreviewIndexFocused],
    );

    const handleKeyDown = useCallback(
        (event: any): void => {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    event.stopPropagation();
                    handleFocus('ArrowDown');
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    event.stopPropagation();
                    handleFocus('ArrowUp');
                    break;
                default:
                    // Do nothing
                    break;
            }
        },
        [handleFocus],
    );

    useEffect(() => {
        currentKeyboardIndex.current = indexFocusedByClick;
        setCurrentPreviewIndexFocused(currentKeyboardIndex.current);
    }, [indexFocusedByClick, setCurrentPreviewIndexFocused]);

    useEffect(() => {
        if (isPreviewVisible) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return (): void => {
            currentKeyboardIndex.current = 0;
            setCurrentPreviewIndexFocused(currentKeyboardIndex.current);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        isPreviewVisible,
        handleKeyDown,
        currentKeyboardIndex,
        setCurrentPreviewIndexFocused,
    ]);

    return {
        handleKeyDown,
    };
}
